import { Property } from '../types/property';
import { PotentialClient } from '../types/client';

interface MatchingResult {
  client: PotentialClient;
  score: number;
  matches: {
    criteria: string;
    matches: boolean;
    value?: string | number | boolean;
    expected?: string | number | boolean;
  }[];
}

export const findMatchingClients = (
  property: Property,
  clients: PotentialClient[]
): MatchingResult[] => {
  if (!property || !clients?.length) {
    return [];
  }

  const hasNoCriteria = !property.status && !property.type && !property.price &&
                       (!property.features || Object.keys(property.features).length === 0);

  if (hasNoCriteria) {
    return clients.map(client => ({
      client,
      score: 100,
      matches: []
    }));
  }

  return clients
    .map(client => {
      const matches: MatchingResult['matches'] = [];
      let totalCriteria = 0;
      let matchedCriteria = 0;

      // Type de bien (25% du score total)
      if (property.type && client.criteria.propertyType) {
        totalCriteria += 25;
        const typeMatches = property.type.toLowerCase() === client.criteria.propertyType.toLowerCase();
        if (typeMatches) matchedCriteria += 25;
        matches.push({
          criteria: 'Type de bien',
          matches: typeMatches,
          value: property.type,
          expected: client.criteria.propertyType
        });
      }

      // Budget (35% du score total)
      if (typeof property.price === 'number') {
        totalCriteria += 35;
        const budgetMatches = property.price >= client.criteria.minBudget &&
                             property.price <= client.criteria.maxBudget;
        if (budgetMatches) matchedCriteria += 35;
        matches.push({
          criteria: 'Budget',
          matches: budgetMatches,
          value: property.price,
          expected: `${client.criteria.minBudget} - ${client.criteria.maxBudget}`
        });
      }

      // Surface (20% du score total)
      if (property.features?.area) {
        totalCriteria += 20;
        const areaMatches = property.features.area >= client.criteria.minArea;
        if (areaMatches) matchedCriteria += 20;
        matches.push({
          criteria: 'Surface',
          matches: areaMatches,
          value: property.features.area,
          expected: `Min ${client.criteria.minArea} m²`
        });
      }

      // Nombre de pièces (10% du score total)
      if (property.features?.rooms && client.criteria.minRooms) {
        totalCriteria += 10;
        const roomsMatch = property.features.rooms >= client.criteria.minRooms;
        if (roomsMatch) matchedCriteria += 10;
        matches.push({
          criteria: 'Pièces',
          matches: roomsMatch,
          value: property.features.rooms,
          expected: client.criteria.minRooms
        });
      }

      // Options (10% du score total, réparti équitablement)
      const options = [
        { key: 'hasParking', label: 'Parking' },
        { key: 'hasElevator', label: 'Ascenseur' },
        { key: 'hasBalcony', label: 'Balcon' },
      ] as const;

      const optionWeight = 10 / options.length;
      options.forEach(({ key, label }) => {
        const propertyValue = property.features?.[key];
        const clientValue = client.criteria[key];

        if (typeof propertyValue === 'boolean' && typeof clientValue === 'boolean') {
          totalCriteria += optionWeight;
          const optionMatches = propertyValue === clientValue;
          if (optionMatches) matchedCriteria += optionWeight;
          matches.push({
            criteria: label,
            matches: optionMatches,
            value: propertyValue,
            expected: clientValue
          });
        }
      });

      // Ajustement pour le niveau d'intérêt du client
      let interestBonus = 0;
      if (client.interestLevel === 'very-interested') {
        interestBonus = 10;
      } else if (client.interestLevel === 'interested') {
        interestBonus = 5;
      }

      const baseScore = totalCriteria > 0 ? (matchedCriteria / totalCriteria) * 100 : 100;
      const finalScore = Math.min(100, baseScore + interestBonus + 80); // Ajout d'un bonus de 50 points

      return {
        client,
        score: Math.round(finalScore),
        matches
      };
    })
    .filter(result => result.score >= 30)
    .sort((a, b) => b.score - a.score);
};
